// Helper functions for parsing special item configurations
function parseStringField(line: string, field: string): string {
    const regex = new RegExp(`${field}=((?<value>[^"\\s][^;\\s]+)|"(?<value>[^"]+)")`, 'i');
    const match = line.match(regex);
    return match?.groups?.value || '';
}

function parseDuration(line: string, field: string): { valid: boolean; error?: string } {
    const minsRegex = '(?<mins>[0-9]+)m';
    const secsRegex = '(?<secs>[0-9\\.]+)s';
    const regex = new RegExp(`${field}=(${minsRegex}${secsRegex}|${minsRegex}|${secsRegex})`, 'i');
    const match = line.match(regex);
    
    if (!match) {
        return { valid: false, error: `Did not understand duration. Example: ${field}=01m02s` };
    }
    
    return { valid: true };
}

function validateFixedTimeMarker(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.toLowerCase().startsWith('fixed time marker')) {
        return { valid: false, errors: ['Special item configuration must start with "Fixed Time Marker"'] };
    }
    
    const typeField = parseStringField(config, 'Type');
    if (!typeField || !['Hard', 'Soft', 'NotBefore'].includes(typeField)) {
        errors.push('Fixed Time Marker Type must be "Hard", "Soft", or "NotBefore". Example: Type=Hard');
    }
    
    const targetTimeResult = parseDuration(config, 'TargetTime');
    if (!targetTimeResult.valid) {
        errors.push('Fixed Time Marker TargetTime is required. ' + targetTimeResult.error);
    }
    
    return { valid: errors.length === 0, errors };
}

function validateBreakNote(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.toLowerCase().startsWith('break note')) {
        return { valid: false, errors: ['Special item configuration must start with "Break Note"'] };
    }

    const durationResult = parseDuration(config, 'Duration');
    if (!durationResult.valid) {
        errors.push('Break Note Duration is required. ' + durationResult.error);
    }

    return { valid: errors.length === 0, errors };
}

function validateVoiceTrack(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const configLower = config.toLowerCase();
    
    if (!configLower.startsWith('voice track')) {
        return { valid: false, errors: ['Special item configuration must start with "Voice Track" or "Voice Track Placeholder"'] };
    }
    
    if (configLower.startsWith('voice track placeholder')) {
        const durationResult = parseDuration(config, 'Duration');
        if (!durationResult.valid) {
            errors.push('Voice Track Placeholder Duration is required. ' + durationResult.error);
        }
    } else {
        const placeholderDurationResult = parseDuration(config, 'PlaceholderDuration');
        if (!placeholderDurationResult.valid) {
            errors.push('Voice Track PlaceholderDuration is required. ' + placeholderDurationResult.error);
        }
    }
    
    return { valid: errors.length === 0, errors };
}

function validateRemoteUrl(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const configLower = config.toLowerCase();
    
    if (!configLower.startsWith('url') && !configLower.startsWith('remote url')) {
        return { valid: false, errors: ['Special item configuration must start with "URL" or "Remote URL"'] };
    }
    
    const durationResult = parseDuration(config, 'Duration');
    if (!durationResult.valid) {
        errors.push('Remote URL Duration is required. ' + durationResult.error);
    }
    
    const url = parseStringField(config, 'URL');
    if (!url) {
        errors.push('Remote URL URL field is required. Example: URL="https://example.com/stream"');
    }
    
    const urlType = parseStringField(config, 'Type');
    if (!urlType || !['LiveStream', 'Live', 'File'].includes(urlType)) {
        errors.push('Remote URL Type must be "LiveStream" or "File". Example: Type=LiveStream');
    }
    
    return { valid: errors.length === 0, errors };
}

function validateAuxInput(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.toLowerCase().startsWith('aux input')) {
        return { valid: false, errors: ['Special item configuration must start with "Aux Input"'] };
    }
    
    const durationResult = parseDuration(config, 'Duration');
    if (!durationResult.valid) {
        errors.push('Aux Input Duration is required. ' + durationResult.error);
    }
    
    const deviceName = parseStringField(config, 'DeviceName');
    const deviceInfo = parseStringField(config, 'DeviceInfo');
    if (!deviceName && !deviceInfo) {
        errors.push('Aux Input DeviceName or DeviceInfo is required. Example: DeviceName="Line In"');
    }
    
    return { valid: errors.length === 0, errors };
}

function validateScheduledEventAction(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.toLowerCase().startsWith('scheduled event action')) {
        return { valid: false, errors: ['Special item configuration must start with "Scheduled Event Action"'] };
    }
    
    const id = parseStringField(config, 'ID');
    if (!id) {
        errors.push('Scheduled Event Action ID is required. Example: ID="event-123"');
    }
    
    return { valid: errors.length === 0, errors };
}

function validateGeneratedVoiceBreak(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const configLower = config.toLowerCase();

    if (!configLower.startsWith('generated voice break') && !configLower.startsWith('futuri audioai breaknote')) {
        return { valid: false, errors: ['Special item configuration must start with "Generated Voice Break"'] };
    }

    // Validate identifier field based on prefix
    if (configLower.startsWith('futuri audioai breaknote')) {
        const breaknoteName = parseStringField(config, 'BreaknoteName');
        if (!breaknoteName) {
            errors.push('Futuri AudioAI BreakNote BreaknoteName is required. Example: BreaknoteName="break1"');
        }
    } else {
        const identifier = parseStringField(config, 'Identifier');
        if (!identifier) {
            errors.push('Generated Voice Break Identifier is required. Example: Identifier="break1"');
        }
    }

    const placeholderDurationResult = parseDuration(config, 'PlaceholderDuration');
    if (!placeholderDurationResult.valid) {
        errors.push('Generated Voice Break PlaceholderDuration is required. ' + placeholderDurationResult.error);
    }

    return { valid: errors.length === 0, errors };
}

function validateAdvertBlock(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.toLowerCase().startsWith('advert block')) {
        return { valid: false, errors: ['Special item configuration must start with "Advert Block"'] };
    }
    
    const startTimeResult = parseDuration(config, 'StartTime');
    if (!startTimeResult.valid) {
        errors.push('Advert Block StartTime is required. ' + startTimeResult.error);
    }
    
    const durationResult = parseDuration(config, 'Duration');
    if (!durationResult.valid) {
        errors.push('Advert Block Duration is required. ' + durationResult.error);
    }
    
    const slotsRegex = /Slots=([0-9]+)/i;
    const slotsMatch = config.match(slotsRegex);
    if (!slotsMatch) {
        errors.push('Advert Block Slots is required. Example: Slots=3');
    }
    
    return { valid: errors.length === 0, errors };
}

function validateHookSequence(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.toLowerCase().startsWith('hook sequence')) {
        return { valid: false, errors: ['Special item configuration must start with "Hook Sequence"'] };
    }

    const name = parseStringField(config, 'Name');
    if (!name) {
        errors.push('Hook Sequence Name is required. Example: Name="My Hook Sequence"');
    }

    const numberOfSongsRegex = /NumberOfSongs=([0-9]+)/i;
    const numberOfSongsMatch = config.match(numberOfSongsRegex);
    if (!numberOfSongsMatch) {
        errors.push('Hook Sequence NumberOfSongs is required. Example: NumberOfSongs=5');
    }

    const targetDurationResult = parseDuration(config, 'TargetDuration');
    if (!targetDurationResult.valid) {
        errors.push('Hook Sequence TargetDuration is required. ' + targetDurationResult.error);
    }

    const validTypes = ['TrackGroupRandom', 'ComingUp', 'ComingUpReversed', 'ComingUpRandom'];
    const type = parseStringField(config, 'Type');
    if (!type || !validTypes.includes(type)) {
        errors.push('Hook Sequence Type must be "TrackGroupRandom", "ComingUp", "ComingUpReversed", or "ComingUpRandom". Example: Type=ComingUp');
    }

    // Conditional validation based on type
    if (type === 'TrackGroupRandom') {
        const trackGroupGuid = parseStringField(config, 'TrackGroupRandomTrackGroup');
        if (!trackGroupGuid) {
            errors.push('Hook Sequence TrackGroupRandomTrackGroup is required when Type=TrackGroupRandom. Example: TrackGroupRandomTrackGroup="12345678-1234-1234-1234-123456789012"');
        }
    } else if (validTypes.includes(type)) {
        // For ComingUp, ComingUpReversed, ComingUpRandom - require either ComingUpDuration or ComingUpSongs
        const comingUpDurationResult = parseDuration(config, 'ComingUpDuration');
        const comingUpSongsRegex = /ComingUpSongs=([0-9]+)/i;
        const comingUpSongsMatch = config.match(comingUpSongsRegex);

        if (!comingUpDurationResult.valid && !comingUpSongsMatch) {
            errors.push('Hook Sequence requires either ComingUpDuration or ComingUpSongs. Example: ComingUpDuration=01m02s or ComingUpSongs=5');
        }
    }

    return { valid: errors.length === 0, errors };
}

function validateMergeStart(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.toLowerCase().startsWith('merge start')) {
        return { valid: false, errors: ['Special item configuration must start with "Merge Start"'] };
    }
    
    const startTimeResult = parseDuration(config, 'StartTime');
    if (!startTimeResult.valid) {
        errors.push('Merge Start StartTime is required. ' + startTimeResult.error);
    }
    
    return { valid: errors.length === 0, errors };
}

function validateMergeEnd(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!config.toLowerCase().startsWith('merge end')) {
        return { valid: false, errors: ['Special item configuration must start with "Merge End"'] };
    }
    
    return { valid: errors.length === 0, errors };
}

function validateSpecialItemConfiguration(config: string, lineNumber: number): { valid: boolean; errors: string[] } {
    const configLower = config.toLowerCase();
    
    if (configLower.startsWith('fixed time marker')) {
        return validateFixedTimeMarker(config, lineNumber);
    } else if (configLower.startsWith('break note')) {
        return validateBreakNote(config, lineNumber);
    } else if (configLower.startsWith('voice track')) {
        return validateVoiceTrack(config, lineNumber);
    } else if (configLower.startsWith('url') || configLower.startsWith('remote url')) {
        return validateRemoteUrl(config, lineNumber);
    } else if (configLower.startsWith('aux input')) {
        return validateAuxInput(config, lineNumber);
    } else if (configLower.startsWith('scheduled event action')) {
        return validateScheduledEventAction(config, lineNumber);
    } else if (configLower.startsWith('generated voice break') || configLower.startsWith('futuri audioai breaknote')) {
        return validateGeneratedVoiceBreak(config, lineNumber);
    } else if (configLower.startsWith('advert block')) {
        return validateAdvertBlock(config, lineNumber);
    } else if (configLower.startsWith('hook sequence')) {
        return validateHookSequence(config, lineNumber);
    } else if (configLower.startsWith('merge start')) {
        return validateMergeStart(config, lineNumber);
    } else if (configLower.startsWith('merge end')) {
        return validateMergeEnd(config, lineNumber);
    } else if (configLower.startsWith('unscheduled')) {
        return { valid: true, errors: [] };
    } else {
        return {
            valid: false,
            errors: ['Unknown special item type. Valid types: Fixed Time Marker, Break Note, Voice Track, Voice Track Placeholder, URL, Remote URL, Aux Input, Scheduled Event Action, Generated Voice Break, Advert Block, Hook Sequence, Merge Start, Merge End, Unscheduled']
        };
    }
}

export function parsePlayoutLog(playoutLogInput: string): ParseResult {
    const results: ValidationResult[] = [];
    const commentLines: number[] = [];
    const items: ParsedItem[] = [];
    let isValid = true;
    let delimiter = '\t';

    // Utility function to push validation results
    function addResult(message: string, type: 'info' | 'warning' | 'error' | 'success', line: number | undefined = undefined, prepend = false) {
        if (prepend) {
            results.unshift({ message, type, line });
        } else {
            results.push({ message, type, line });
        }

        if (type === 'error') {
            isValid = false;
        }
    }

    // Check file encoding - (This is outside the scope of JavaScript as it handles string, assuming the input is already UTF-8 encoded)

    
    // Check for required header
    if (!playoutLogInput.startsWith('-- PlayIt Playout Log Format 2.0 --')) {
        addResult('Missing or incorrect header. Expected:\r\n-- PlayIt Playout Log Format 2.0 --', 'error');
    } else {

        const lines = playoutLogInput.split(/\r\n|\n/);
        let hourCount = 0;
        let itemCount = 0;
        let delimiterDefined = false;
        let currentHour = '';

        let lineNumber = 0;
        for (const line of lines) {
            lineNumber++;

            const lineTrimmed = line.replace(/\s+$/, '');

            // Skip empty lines
            if (lineTrimmed === '') {
                continue;
            }

            // if line starts with #, skip it
            if (lineTrimmed.startsWith('#')) {
                commentLines.push(lineNumber);
                continue;
            }

            if (lineTrimmed.startsWith('--')) {
                // Check delimiter
                if (lineTrimmed.startsWith('-- Delimiter=')) {
                    const delimiterMatch = lineTrimmed.match(/-- Delimiter=(.*?) --/);
                    if (delimiterMatch) {
                        delimiter = delimiterMatch[1] === 'TAB' ? '\t' : delimiterMatch[1];
                        delimiterDefined = true;
                    }

                    if (delimiter === ',' || delimiter === ';') {
                        addResult('Comma (,) or semi-colon (;) is not recommended as a delimiter.', 'warning', lineNumber);
                    }
                }


                if (lineTrimmed.startsWith('-- Hour ')) {
                    // check it is either -- Hour or -- Hour yyyy-MM-ddTHH:mm:ss (o) --
                    const hourMatch = lineTrimmed.match(/-- Hour( (.*?))? --/);
                    if (!hourMatch) {
                        addResult(`Hour is not non-defined:\r\n-- Hour --\r\nor in the format\r\n-- Hour yyyy-MM-ddTHH:mm:ss (o) --\r\ne.g. -- Hour 2021-02-25T22:00:00 (+01) -- for 25th February 2021 at 10pm in the time zone 1 hour ahead of Coordinated Universal Time`, 'error', lineNumber);
                    } else {
                        // new hour
                        hourCount++;
                        currentHour = hourMatch[2] || hourCount.toString();
                    }
                }

                continue;
            }

            itemCount++;

            const columns = line.split(delimiter);
            const startTimeColumn = columns?.[0]?.trim();
            const trackIdColumn = columns?.[1]?.trim();
            const specialItemConfigurationColumn = columns?.[2]?.trim();
            const reconciliationIdColumn = columns?.[3]?.trim();
            const segueInfoColumn = columns?.[4]?.trim();

            // Check for whitespace in individual columns
            if (columns[0] && columns[0] != columns[0].trim()) {
                addResult(`Start time column contains leading or trailing whitespace which may cause issues.`, 'warning', lineNumber);
            }
            if (columns[1] && columns[1] != columns[1].trim()) {
                addResult(`Track ID column contains leading or trailing whitespace which may cause issues with Track ID matching.`, 'warning', lineNumber);
            }
            if (columns[2] && columns[2] != columns[2].trim()) {
                addResult(`Special Item Configuration column contains leading or trailing whitespace which may cause issues.`, 'warning', lineNumber);
            }
            if (columns[3] && columns[3] != columns[3].trim()) {
                addResult(`Reconciliation ID column contains leading or trailing whitespace which may cause issues with Reconciliation ID matching.`, 'warning', lineNumber);
            }
            if (columns[4] && columns[4] != columns[4].trim()) {
                addResult(`Segue Info column contains leading or trailing whitespace which may cause issues.`, 'warning', lineNumber);
            }

            // Check start time format is in the format HH:mm:ss (24 hour clock)
            const startTimeMatch = startTimeColumn.match(/^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/);
            if (startTimeColumn && !startTimeMatch) {
                addResult(`Start time is not in the format HH:mm:ss (24-hour clock)`, 'error', lineNumber);
                continue;
            }

            // check there is at least one track ID or special item configuration
            if (!trackIdColumn && !specialItemConfigurationColumn) {
                addResult(`Track ID or Special Item Configuration is required`, 'error', lineNumber);
                continue;
            }

            // Track ID must be empty if Special Item Configuration is not
            if (trackIdColumn && specialItemConfigurationColumn) {
                addResult(`Track ID and Special Item Configuration cannot both be set`, 'error', lineNumber);
                continue;
            }

            // Validate special item configuration if present
            if (specialItemConfigurationColumn) {
                const validationResult = validateSpecialItemConfiguration(specialItemConfigurationColumn, lineNumber);
                if (!validationResult.valid) {
                    for (const error of validationResult.errors) {
                        addResult(`${error}`, 'error', lineNumber);
                    }
                    continue;
                }
            }

            items.push({
                hour: currentHour,
                startTime: startTimeColumn,
                trackId: trackIdColumn,
                specialItemConfiguration: specialItemConfigurationColumn,
                reconciliationId: reconciliationIdColumn,
                segueInfo: segueInfoColumn
            });
        }


        if (!delimiterDefined) {
            addResult('Delimiter not defined. Assuming TAB. Define a delimiter with:\r\n-- Delimiter=TAB --', 'info');
        }

        if (hourCount === 0) {
            addResult('No hours found. Expected at least one:\r\n-- Hour --', 'error');
        }

        if(hourCount > 0 && itemCount == 0) {
            addResult('No items found.', 'warning');
        }

    }

    if (isValid) {
        addResult('Input is in the valid format.', 'success', undefined, true);
    }

    return {
        isValid,
        commentLines,
        validationResults: results,
        items: items,
        delimiter
    };
}

export interface ParseResult {
    validationResults: ValidationResult[];
    commentLines: number[];
    items: ParsedItem[];
    isValid: boolean;
    delimiter: string;
}

export interface ValidationResult {
    message: string;
    type: 'info' | 'warning' | 'error' | 'success',
    line?: number;
}

export interface ParsedItem {
    hour: string;
    startTime: string;
    trackId: string;
    specialItemConfiguration: string;
    reconciliationId: string;
    segueInfo: string
}